import { memo } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Flex, Text } from '@mantine/core';

import { getReportName } from '@/utils/constants';
import { formatUtcDate } from '@/utils/formatDate';
import { routes } from '@/routes';
import ActivateReportIcon from '@/assets/icons/ActivateReportIcon';
import { GrayAlertIcon } from '@/assets/icons/GrayAlertIcon';
import { GreenAlertIcon } from '@/assets/icons/GreenAlertIcon';
import { RedAlertIcon } from '@/assets/icons/RedAlertIcon';
import { YellowAlertIcon } from '@/assets/icons/YellowAlertIcon';

import { MiniTxReport, ReportColor, SignatureStatus } from '@/types/transmissionReport';
import IconButton from '@/ui/IconButton';

import styles from './PatientsReportList.module.scss';

const PatientReportCard = memo(
  ({
    report,
    handleDownloadReport,
    handleActiveReport
  }: {
    report: MiniTxReport;
    handleDownloadReport: (report: MiniTxReport) => void;
    handleActiveReport: (report: number) => void;
  }) => {
    const ReportIcon = () => {
      switch (report.color) {
        case ReportColor.Red:
          return <RedAlertIcon size={30} />;
        case ReportColor.Yellow:
          return <YellowAlertIcon size={30} />;
        case ReportColor.Green:
          return <GreenAlertIcon size={30} />;
        default:
          return <GrayAlertIcon size={30} />;
      }
    };

    const SignatureStatusBadge = () => {
      switch (report.signatureStatus) {
        case SignatureStatus.Signed:
        case SignatureStatus.SignatureNotRequired:
          return (
            <Badge color="green" variant="filled" bd={`1px solid darkGreen`}>
              Complete
            </Badge>
          );
        case SignatureStatus.NonPractitionerSigned:
          return (
            <Badge color="sky" variant="filled" bd={`1px solid blue`}>
              Pending Sig
            </Badge>
          );
        case SignatureStatus.Dismissed:
          return (
            <Badge color="gray" variant="filled" bd={`1px solid gray`}>
              Dismissed
            </Badge>
          );
        case SignatureStatus.PendingReview:
          return (
            <Badge color="#FF644E" variant="filled" bd={`1px solid #FF644E`}>
              Pending Review
            </Badge>
          );
        default:
          return (
            <Badge color="yellow" variant="filled" bd={`1px solid yellow.8`}>
              Draft
            </Badge>
          );
      }
    };

    const reportName = getReportName(
      report?.deviceType,
      report?.transmissionType,
      report?.color,
      report?.reportType === 'HEART_FAILURE'
    );

    return (
      <Flex className={styles.patientReportCard} gap={4} justify={'space-between'} align={'center'}>
        <Flex gap={'sm'} align={'center'}>
          <Flex align={'center'} justify={'center'}>
            <ReportIcon />
          </Flex>
          <Flex direction={'column'} justify={'space-between'} gap={4}>
            <Flex justify="space-between" align="center" direction="row" w="100%">
              <Flex align="center" gap="xs">
                <Text fw={600} fz={14} c={'neutral.8'} lh={1}>
                  {reportName}
                </Text>
                <SignatureStatusBadge />
                {report?.isBillable && (
                  <Badge color="plum.3" variant="filled" bd={`1px solid plum.4`}>
                    Billable
                  </Badge>
                )}
              </Flex>
            </Flex>
            <Flex gap="md" align="center">
              <Text fz={12} c="plum.5" lh={1.2} fw={500}>
                Transmitted on{' '}
                {report?.transmissionDate
                  ? `${formatUtcDate(report?.transmissionDate, 'M/DD/YY, h:mm A')} (UTC)`
                  : 'Unassigned'}
              </Text>
              <Text fz={12} c="plum.5" lh={1.2} fw={500}>
                Date of Service:{' '}
                {report?.dateOfService
                  ? formatUtcDate(report.dateOfService, 'M/DD/YY')
                  : 'Unassigned'}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex gap={'xs'} align={'center'}>
          {report.signatureStatus === SignatureStatus.Dismissed ? (
            <Flex align={'center'}>
              <IconButton
                icon="download"
                onClick={() => {
                  handleDownloadReport(report);
                }}
              />
              <span
                className={styles.dismissedReport}
                onClick={() => handleActiveReport(report?.id)}>
                <ActivateReportIcon />
              </span>
            </Flex>
          ) : (
            <Flex align={'center'}>
              <IconButton
                icon="download"
                onClick={() => {
                  handleDownloadReport(report);
                }}
              />
              {report.signatureStatus !== SignatureStatus.SignatureNotRequired && (
                <Link
                  to={routes.physician.transmissionReportDetail(report?.id.toString())}
                  target="_blank">
                  <IconButton icon="externalLink" />
                </Link>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  }
);

PatientReportCard.displayName = 'PatientReportCard';

export default PatientReportCard;
